// ─────────────────────────────────────────────
// Oike · Mi marca — hand-drawn strokes of identity A (pure). Each function returns SVG path data in page
// coordinates (y down). The same strings are drawn in the preview (SVG) and in the PDF (pdf-lib › drawSvgPath),
// so both look the same. The wobble is deterministic: a seed per element, never random at draw time.
// Strokes have a function: underline (emphasize), loop (highlight), arrow (connect), block (mark a key word),
// sparks (draw the eye to one word). They never cover an image zone: the layout decides where they go.
// ─────────────────────────────────────────────

/** A small deterministic generator (Park–Miller) from a numeric seed. */
export function rng(seed) {
  let s = (Math.abs(Math.floor(seed)) % 2147483646) + 1;
  return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; };
}
/** A stable numeric seed from any text (FNV-1a). */
export function seedOf(text) {
  let h = 0x811c9dc5;
  for (const ch of String(text)) { h ^= ch.charCodeAt(0); h = Math.imul(h, 0x01000193) >>> 0; }
  return h;
}
const f = (n) => (Math.round(n * 100) / 100).toString();

/** A line that wobbles a little: returns one or two passes (a marker goes over twice). */
export function underline(x1, y1, x2, y2, seed, { wob = 3, passes = 2 } = {}) {
  const r = rng(seed); const j = (a) => (r() - 0.5) * 2 * a;
  const out = [];
  for (let k = 0; k < passes; k++) {
    const mx = (x1 + x2) / 2 + j(wob), my = (y1 + y2) / 2 + j(wob);
    out.push({ d: `M${f(x1 + j(1.5))} ${f(y1 + j(1.5))} Q${f(mx)} ${f(my)} ${f(x2 + j(1.5))} ${f(y2 + j(1.5))}`, pass: k });
  }
  return out;
}

/** An ellipse that does not close perfectly, drawn smooth (low-frequency wobble). */
export function loop(cx, cy, rx, ry, seed, { passes = 2 } = {}) {
  const r = rng(seed); const j = (a) => (r() - 0.5) * 2 * a;
  const out = [];
  for (let k = 0; k < passes; k++) {
    const start = -0.4 + j(0.3), turns = 1.08 + j(0.04), ph = r() * 6;
    const pts = [];
    for (let t = 0; t <= 64; t++) {
      const a = start + (t / 64) * Math.PI * 2 * turns;
      const k2 = 1 + 0.045 * Math.sin(a * 2 + ph) + 0.02 * Math.sin(a * 3 + ph * 2) + j(0.005);
      pts.push(`${f(cx + Math.cos(a) * rx * k2)} ${f(cy + Math.sin(a) * ry * k2)}`);
    }
    out.push({ d: `M${pts[0]} L${pts.slice(1).join(' L')}`, pass: k });
  }
  return out;
}

/** A short curved arrow with a hand-drawn head. bend: how much it curves (signed). */
export function arrow(x1, y1, x2, y2, seed, { bend = 12, head = 9 } = {}) {
  const r = rng(seed); const j = (a) => (r() - 0.5) * 2 * a;
  const len = Math.hypot(x2 - x1, y2 - y1) || 1;
  const mx = (x1 + x2) / 2 - ((y2 - y1) / len) * bend + j(2), my = (y1 + y2) / 2 + ((x2 - x1) / len) * bend + j(2);
  const ang = Math.atan2(y2 - my, x2 - mx);
  const heads = [-1, 1].map(s => { const a = ang + Math.PI + s * (0.45 + j(0.06)); return `M${f(x2)} ${f(y2)} L${f(x2 + Math.cos(a) * head)} ${f(y2 + Math.sin(a) * head)}`; });
  return [{ d: `M${f(x1)} ${f(y1)} Q${f(mx)} ${f(my)} ${f(x2)} ${f(y2)}`, pass: 0 }, ...heads.map(d => ({ d, pass: 0 }))];
}

/** A marker block behind a word: slightly tilted and uneven (filled). */
export function block(x, y, w, h, seed) {
  const r = rng(seed); const j = (a) => (r() - 0.5) * 2 * a;
  return `M${f(x + j(3))} ${f(y + j(3))} L${f(x + w + j(4))} ${f(y + 4 + j(3))} L${f(x + w - 6 + j(3))} ${f(y + h + j(3))} L${f(x + 3 + j(3))} ${f(y + h - 3 + j(3))} Z`;
}

/** Three short strokes that point at a word (from the left by default). */
export function sparks(cx, cy, seed, { r: radius = 22, dir = -2.5, spread = 1.2, n = 3 } = {}) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const a = dir + (i - (n - 1) / 2) * (spread / n);
    out.push(...underline(cx + Math.cos(a) * radius * 0.5, cy + Math.sin(a) * radius * 0.5, cx + Math.cos(a) * radius, cy + Math.sin(a) * radius, seed + i * 13, { wob: 1.2, passes: 1 }));
  }
  return out;
}

/** A short dash used as a list bullet. */
export const dash = (x, y, w = 10) => `M${f(x)} ${f(y + 1)} L${f(x + w)} ${f(y - 0.6)}`;
